import { useMemo } from "react";

import {
  faCheck,
  faClock,
  faCube,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { times } from "lodash";
import { twJoin, twMerge } from "tailwind-merge";

import { GenericObject } from "src/api/flowTypes";
import { Icon } from "src/base-components/Icon";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TableComp } from "src/base-components/Table";
import {
  EventConfigEnriched,
  PropertyDefinitionOutput,
} from "src/clients/features-control";
import { INPUT_LIST, OUTPUT_LIST } from "src/constants/InternalDataKeys";
import { Cell } from "src/databaseDebugPopover/BaseDebugPopover";
import {
  AvailableData,
  DataShape,
  RowShape,
  RowStatusType,
  RowType,
  TestResultPopoverProps,
} from "src/dataTable/DetailedView/tabs/types";
import { useIterationsData } from "src/dataTable/DetailedView/tabs/useIterationsData";
import { buildIterationRows } from "src/dataTable/DetailedView/tabs/utils";
import { HeaderCell } from "src/dataTable/HeaderCell";
import { IntermediateResultsHeaderCell } from "src/dataTable/IntermediateResultsHeaderCell";
import { ResultCell } from "src/dataTable/ResultCell";
import {
  entityPropToSubColumn,
  getColumnNames,
  getHeaderSchemaIcon,
} from "src/dataTable/TableUtils";
import { CellContent } from "src/dataTable/types";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import {
  EntitySchemaProperty,
  EntitySchemaResource,
  useEntitySchemas,
} from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import {
  useWorkspaceFeatureGates,
  WorkspaceFeatureGates,
} from "src/hooks/useWorkspaceFeatureGates";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { assertUnreachable } from "src/utils/typeUtils";

const columnHelper = createColumnHelper<RowShape>();

export const LoopIterations: React.FC<
  Omit<TestResultPopoverProps, "open" | "onClose" | "nodeID"> & {
    isFetching: boolean;
  }
> = ({ data, rowData, selectedNodeId, error: historyError, isFetching }) => {
  const { availableData, status, error } = useIterationsData({
    error: historyError,
    rowData,
    data,
    selectedNodeId,
  });

  return (
    <InspectIterationsBody
      availableData={availableData}
      errorMessage={error}
      isFetching={isFetching}
      status={status}
    />
  );
};

const InspectIterationsBody: React.FC<{
  availableData: AvailableData;
  status: RowStatusType;
  errorMessage: string | false;
  isFetching?: boolean;
}> = ({ availableData, status, errorMessage, isFetching = false }) => {
  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useAuthoringContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
    options: {
      enabled: featureGates.entitiesEnabled,
    },
  });
  const [environment] = useEnvironment();

  const { data: eventSchemas } = useEventSchemas();

  const rows = useMemo(
    () =>
      buildIterationRows(
        availableData[INPUT_LIST],
        availableData[OUTPUT_LIST],
        availableData.item,
        status,
      ),
    [availableData, status],
  );

  const columns = useMemo(() => {
    if (!availableData && !isFetching) {
      return [];
    }

    if (isFetching) {
      return times(4, (i) => ({
        id: `column${i}`,
        header: () => (
          <Header header={<SkeletonPlaceholder height="h-4" width="w-4/5" />} />
        ),
        accessorFn: () => "",
        cell: () => <span />,
      }));
    }

    return buildColumnsDef({
      inputList: availableData[INPUT_LIST],
      outputList: availableData[OUTPUT_LIST],
      featureGates,
      entitySchemas: entitySchemas?.entities,
      eventSchemas,
      environment,
    });
  }, [
    availableData,
    isFetching,
    entitySchemas,
    eventSchemas,
    featureGates,
    environment,
  ]);

  const isFailure = status === "failure";
  const hadEarlyExit =
    status === "success" && availableData[INPUT_LIST]?.length !== rows.length;
  const isEmpty = rows.length < 1 && !isFetching;
  const dataLoc = (() => {
    if (isFailure) {
      return "iteration-pill-failure";
    }

    return isEmpty ? "iteration-pill-empty" : "iteration-pill-break";
  })();

  return (
    <>
      {!isFetching && (isEmpty || hadEarlyExit || isFailure) && (
        <div
          className={twJoin(
            "flex flex-col gap-y-4 pt-2",
            "decideScrollbar min-h-0",
            isEmpty ? "pb-6" : "pb-4",
          )}
        >
          <Callout dataLoc={dataLoc} type={isFailure ? "error" : "neutral"}>
            {isFailure && errorMessage}
            {isEmpty &&
              "Loop iteration results cannot be viewed since the list being looped over is empty."}
            {hadEarlyExit &&
              `The loop terminated early after meeting the break condition during the iteration with index ${
                rows.length - 1
              }.`}
          </Callout>
          {isEmpty && (
            <div className="flex w-full items-center justify-center rounded-lg border border-gray-200">
              <EmptyState
                description="The field you intended to loop over is empty. Select a field with items to enable loop iterations."
                headline="No loop iterations"
                icon={faCube}
              />
            </div>
          )}
        </div>
      )}
      {!isEmpty && (
        <div
          className={twJoin(
            "w-full bg-white",
            !hadEarlyExit && !isFailure && "pt-4",
            "flex h-full min-h-0 flex-col pb-4",
          )}
        >
          <TableComp
            columns={columns}
            data={rows}
            dataLoc="iterations-table"
            frameClassName="h-full min-h-0"
            headerPropsGetter={(headerGroup) => ({
              classNameOverrides: twJoin(
                headerGroup.depth === 0 &&
                  "[&>th>div]:border-gray-100 [&>th>div]:border-y",
                headerGroup.depth === 1 &&
                  "[&>th>div]:border-gray-100 [&>th>div]:border-b",
                "static border-b-0 [&>th:first-child>div]:border-gray-100 [&>th:first-child>div]:border-l [&>th:first-child]:sticky [&>th:first-child]:left-0",
              ),
            })}
            isLoading={isFetching}
            loadingRowsCount={9}
            rowClassName={twMerge(
              "border-gray-100 border-b [&>td:first-child>div]:border-gray-100 [&>td:first-child>div]:border-l [&>td:first-child]:sticky [&>td:first-child]:left-0",
              isFetching &&
                "border-gray-100 border-r [&>td:not(:last-child)>div]:border-gray-100 [&>td:not(:last-child)>div]:border-r",
            )}
          />
        </div>
      )}
    </>
  );
};

const buildObjectColumns = ({
  list,
  featureGates,
  eventSchemas,
  entitySchemas,
  isInput,
  environment,
}: {
  list: Omit<DataShape, string | number>[];
  featureGates: WorkspaceFeatureGates | undefined;
  eventSchemas: EventConfigEnriched[] | undefined;
  entitySchemas: EntitySchemaResource[] | undefined;
  isInput: boolean;
  environment: Environment;
}): ColumnDef<RowShape, React.ReactNode>[] => {
  const columnNames = getColumnNames(list as Record<string, unknown>[]);

  return columnNames.map(([key, decodedTypeHint]) => {
    if (
      (!isInput &&
        decodedTypeHint?.metaType === "entity" &&
        featureGates?.entitiesEnabled) ||
      (decodedTypeHint?.metaType === "event" &&
        featureGates?.featuresEventsEnabled)
    ) {
      let props: [string, EntitySchemaProperty | PropertyDefinitionOutput][] =
        [];
      let eventSchema = undefined;
      let entitySchema = undefined;
      let propKeys = undefined;

      if (decodedTypeHint?.metaType === "event") {
        eventSchema = eventSchemas?.find(
          (schema) => schema.internal_name === decodedTypeHint.configSchemaId,
        );
        props = Object.entries(eventSchema?.properties ?? {});
      } else if (decodedTypeHint?.metaType === "entity") {
        entitySchema = entitySchemas?.find(
          (schema) => schema._id === decodedTypeHint.configSchemaId,
        );
        props = Object.entries(entitySchema?.properties ?? {});
      }
      propKeys = props.map(([propKey]) => propKey);

      return columnHelper.group({
        id: `item-${isInput ? "input" : "output"}-entity-${key}`,
        header: () => (
          <IntermediateResultsHeaderCell
            className="flex items-center gap-x-1"
            dataLoc={`${key}-column`}
          >
            <Icon
              icon={getHeaderSchemaIcon(entitySchema, eventSchema)}
              size="2xs"
            />
            {key}
          </IntermediateResultsHeaderCell>
        ),
        columns: props.map(
          ([propertyName, prop]) =>
            entityPropToSubColumn(
              propertyName,
              prop,
              key,
              undefined,
              propKeys,
              (row: RowShape) => (isInput ? row.input[key] : row.output[key]),
              "bottom",
              environment,
            ) as ColumnDef<RowShape, React.ReactNode>,
        ),
      });
    }

    return {
      id: `item-${isInput ? "input" : "output"}-${key}`,
      header: (ctx) => (
        <Header
          header={ctx.header.depth === 2 ? key : " "}
          borderBottom
          transparent
        />
      ),
      accessorFn: (row) => {
        if (row.type === "skeleton") {
          return pendingCell;
        }

        const value = isInput ? row.input[key] : row.output[key];
        return normalizeCellValue(value) ?? " ";
      },
      cell: ({ cell }) => <CustomResultCell cell={cell} />,
    } as ColumnDef<RowShape, React.ReactNode>;
  });
};

const buildColumnsDef = ({
  inputList,
  outputList,
  featureGates,
  entitySchemas,
  eventSchemas,
  environment,
}: {
  inputList?: DataShape[];
  outputList?: DataShape[];
  featureGates?: WorkspaceFeatureGates;
  entitySchemas?: EntitySchemaResource[];
  eventSchemas?: EventConfigEnriched[];
  environment: Environment;
}): ColumnDef<RowShape, React.ReactNode>[] => {
  const inputColumns = (() => {
    if (!inputList) {
      return [];
    }

    const isObjectList = inputList.every((item) => typeof item === "object");
    if (isObjectList) {
      return buildObjectColumns({
        list: inputList,
        featureGates,
        eventSchemas,
        entitySchemas,
        isInput: true,
        environment,
      });
    } else {
      return {
        id: "item",
        header: (ctx) => (
          <Header
            borderBottom={ctx.header.depth === 3}
            header={ctx.header.depth === 2 ? "item" : " "}
            transparent
          />
        ),
        accessorFn: (row) =>
          row.type === "skeleton"
            ? pendingCell
            : (normalizeCellValue(row.input.item) ?? " "),
        cell: ({ cell }) => <CustomResultCell cell={cell} />,
      } as ColumnDef<RowShape, React.ReactNode>;
    }
  })();

  const outputColumns = (() => {
    if (!outputList) {
      return [];
    }

    const isObjectList = outputList.every((item) => typeof item === "object");
    const outputColumnNames = getColumnNames(
      outputList as Record<string, unknown>[],
    );

    // Edge case when the output list is empty and column names cannot be inferred from
    // the sub-flow response items in the list. In this case, we add a single column with a blank header.
    if (outputColumnNames.length < 1) {
      outputColumnNames.push([" ", null]);
    }

    if (isObjectList) {
      return buildObjectColumns({
        list: outputList,
        featureGates,
        eventSchemas,
        entitySchemas,
        isInput: false,
        environment,
      });
    } else {
      return outputColumnNames.map(
        ([key]) =>
          ({
            id: `output.${key}`,
            header: () => <Header header={key} transparent />,

            accessorFn: (row) => {
              if (row.type === "failure") {
                return " ";
              }
              if (row.type === "skeleton" || row.type === "pending") {
                return pendingCell;
              }
              return normalizeCellValue(row.output[key]);
            },
            cell: ({ cell }) => (
              <CustomResultCell
                cell={cell}
                isFailure={cell.row.original.type === "failure"}
              />
            ),
          }) as ColumnDef<RowShape, React.ReactNode>,
      );
    }
  })();

  return [
    {
      id: "index",
      header: (ctx) => (
        <Header
          borderBottom={ctx.header.depth === 3}
          header={ctx.header.depth === 2 ? "index" : " "}
          transparent={ctx.header.depth !== 2}
        />
      ),
      accessorFn: (row) => row?.index,
      cell: ({ cell }) => (
        <Cell
          cell={cell}
          classNameOverrides={twJoin(
            cell.row.original.type === "skeleton" ? "text-gray-500" : "",
            "sticky left-0 z-10 bg-white",
          )}
        />
      ),
      size: 50,
      maxSize: 50,
    },
    {
      id: "test-status",
      header: (ctx) => (
        <Header
          borderBottom={ctx.header.depth === 3}
          header={ctx.header.depth === 2 ? "Test Status" : " "}
          transparent
        />
      ),
      accessorFn: (row) => {
        if (row.type === "skeleton") {
          return pendingCell;
        } else {
          return <RowStatus type={row.type} />;
        }
      },
      size: 120,
      maxSize: 120,
      minSize: 120,
      cell: ({ cell }) => (
        <Cell cell={cell} classNameOverrides="justify-start" />
      ),
    },
    ...(Array.isArray(inputColumns)
      ? [
          {
            id: "item-parent",
            header: (ctx) =>
              Array.isArray(inputColumns) && (
                <Header
                  header={ctx.header.depth === 1 ? "item" : " "}
                  transparent={ctx.header.depth !== 1}
                />
              ),
            columns: inputColumns,
          } as ColumnDef<RowShape, React.ReactNode>,
        ]
      : [inputColumns]),
    {
      id: "output",
      header: () => <Header header="output" />,
      columns: outputColumns,
    },
  ];
};

const RowStatus: React.FC<{ type: RowType }> = ({ type }) => {
  switch (type) {
    case "failure":
      return (
        <>
          <Icon color="text-red-500" icon={faWarning} size="2xs" /> Failed
        </>
      );

    case "pending":
      return (
        <>
          <Icon color="text-indigo-400" icon={faClock} size="2xs" /> Pending
        </>
      );

    case "ignored":
      return (
        <>
          <Icon color="text-gray-400" icon={faWarning} size="2xs" /> Ignored
        </>
      );

    case "skeleton":
      return pendingCell;

    case "success":
      return (
        <>
          <Icon color="text-green-600" icon={faCheck} size="2xs" /> Successful
        </>
      );

    default:
      assertUnreachable(type);
      return null;
  }
};

const pendingCell = (
  <div className="flex h-full min-w-[104px] items-center gap-x-0.5">
    <SkeletonPlaceholder height="h-3" />
    <div children=" " className="basis-5" />
  </div>
);

const normalizeCellValue = (value: unknown) => {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "object") {
    return JSON.stringify(value);
  } else if (value === undefined) {
    return undefined;
  } else {
    return String(value);
  }
};

const Header: React.FC<{
  header?: React.ReactNode;
  transparent?: boolean;
  borderBottom?: boolean;
}> = ({ header, transparent = false, borderBottom = false }) => (
  <HeaderCell
    classNameOverrides={twJoin(
      "max-w-none border-gray-100 border-r bg-gray-50 py-1.5 pr-1.5 pl-1.5",
      transparent && "bg-white",
      borderBottom && "border-b",
    )}
  >
    {header}
  </HeaderCell>
);

type CustomResultCellProps<T> = {
  cell: CellContent<T>;
  isFailure?: boolean;
};
const CustomResultCell = <T extends GenericObject>({
  cell,
  isFailure,
}: CustomResultCellProps<T>) => {
  return (
    <ResultCell
      cell={cell}
      className={twJoin(
        "max-w-none border-gray-100 border-r py-1.5 pr-2 pl-1.5",
        isFailure && "bg-red-50",
      )}
      objectDetailPosition="topLeft"
    />
  );
};
